import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';

import styles from './HashRoutedSlides.scss';

class HashRoutedSlides extends Component {
  state = {
    slideIndex: -1,
  };

  constructor(props) {
    super(props);

    this.nodeRefs = React.Children.map(props.children, () => createRef());
  }

  componentDidMount() {
    this.updateIndex();
  }

  // Figure out which direction the slides should animate
  componentDidUpdate(prevProps) {
    const { children, location } = this.props;

    const prevHash = prevProps.location.hash;
    const currentHash = location.hash;

    // If the hash has changed then a different slide needs to be shown
    if (prevHash !== currentHash) {
      const prevIndex = React.Children.toArray(children).findIndex(
        slide => slide.props.hash === prevProps.location.hash,
      );

      const currentIndex = React.Children.toArray(children).findIndex(
        slide => slide.props.hash === location.hash,
      );

      const goingForward = currentIndex > prevIndex;

      // Set the slide animation direction before updating the slide to be shown
      // eslint-disable-next-line @eslint-react/no-set-state-in-component-did-update
      this.setState({ goingForward }, this.updateIndex());
    }
  }

  // Set the slideIndex from the URL hash
  updateIndex = () => {
    const { children, location } = this.props;
    const slideIndex = React.Children.toArray(children).findIndex(
      slide => slide.props.hash === location.hash,
    );
    this.setState({ slideIndex });
  };

  // eslint-disable-next-line class-methods-use-this
  transitionFinished = node => {
    // Move focus to the new slide as it is a route change
    node.current?.focus();
  };

  render() {
    const animationDuration = 200; // Needs to match with CSS
    const { children } = this.props;
    const { goingForward, slideIndex } = this.state;

    // Prevent any slides being rendered if the slideIndex hasn't been calculated yet
    if (slideIndex === -1) return null;

    return (
      <div className={styles.container}>
        {React.Children.map(children, (slide, i) => {
          // If the slide index matches then it should be shown
          const isShown = i === slideIndex;

          // Animation direction based on which slide and whether we are going forwards/backwards
          let fromRight = goingForward;
          if (!isShown) {
            fromRight = !fromRight;
          }

          const nodeRef = this.nodeRefs[i];

          return (
            <CSSTransition
              classNames={{
                enter: styles.itemEnter,
                enterActive: styles.itemEnterActive,
                enterDone: styles.itemEnterDone,
                exit: styles.itemExit,
                exitActive: styles.itemExitActive,
              }}
              in={isShown}
              key={slide.props.hash}
              onEntered={() => this.transitionFinished(nodeRef)}
              timeout={animationDuration}
              unmountOnExit
              nodeRef={nodeRef}
            >
              <div
                className={classNames(styles.slide, {
                  [styles.fromRight]: fromRight,
                  [styles.fromLeft]: !fromRight,
                })}
                tabIndex="-1"
                data-testid="hashed-route-slide"
                ref={nodeRef}
              >
                {slide}
              </div>
            </CSSTransition>
          );
        })}
      </div>
    );
  }
}

HashRoutedSlides.propTypes = {
  children: PropTypes.arrayOf(propValue => {
    const allChildrenHaveHashProp = propValue.every(child => typeof child.props.hash === 'string');
    if (!allChildrenHaveHashProp)
      return new Error('All children must have a hash prop which is a string');
    return null;
  }).isRequired,
  location: PropTypes.shape({
    hash: PropTypes.string.isRequired,
  }).isRequired,
};

export default HashRoutedSlides;
